import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import Home from "@/pages/Home";
import Consultant from "@/pages/Consultant";
import Dashboard from "@/pages/org/Dashboard";
import Scoping from "@/pages/org/Scoping";
import Controls from "@/pages/org/Controls";
import Policies from "@/pages/org/Policies";
import PolicyDetail from "@/pages/org/PolicyDetail";
import Evidence from "@/pages/org/Evidence";
import Risks from "@/pages/org/Risks";
import Audits from "@/pages/org/Audits";
import AuditDetail from "@/pages/org/AuditDetail";
import Incidents from "@/pages/org/Incidents";
import Tasks from "@/pages/org/Tasks";
import Team from "@/pages/org/Team";
import AuditPack from "@/pages/org/AuditPack";
import Accreditation from "@/pages/org/Accreditation";
import Registers from "@/pages/org/Registers";
import Notifications from "@/pages/org/Notifications";
import Training from "@/pages/org/Training";
import Vendors from "@/pages/org/Vendors";
import TrustSettings from "@/pages/org/TrustSettings";
import TrustPage from "@/pages/TrustPage";
import VendorQuestionnaire from "@/pages/VendorQuestionnaire";
import Landing from "@/pages/Landing";
import SsoCallback from "@/pages/SsoCallback";
import ConnectorCallback from "@/pages/ConnectorCallback";
import Billing from "@/pages/Billing";
import PaymentResult from "@/pages/PaymentResult";
import Admin from "@/pages/Admin";
import Docs from "@/pages/Docs";
import Support from "@/pages/org/Support";

function Protected({ children }) {
  const { session } = useAuth();
  if (session === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (session === false) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { session } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={session ? <Navigate to="/app" replace /> : <Login />} />
      <Route path="/auth/sso/:provider" element={<SsoCallback />} />
      <Route path="/connectors/:provider/callback" element={<Protected><ConnectorCallback /></Protected>} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/trust/:slug" element={<TrustPage />} />
      <Route path="/vendor/:token" element={<VendorQuestionnaire />} />
      <Route path="/" element={session ? <Navigate to="/app" replace /> : <Landing />} />
      <Route path="/app" element={<Protected><Home /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/admin" element={<Protected>{session?.user?.is_platform_admin ? <Admin /> : <Navigate to="/app" replace />}</Protected>} />
      <Route path="/docs/*" element={<Docs />} />
      <Route path="/docs" element={<Docs />} />
      <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentResult cancelled /></Protected>} />
      <Route path="/consultant" element={<Protected><Consultant /></Protected>} />
      <Route path="/org/:orgId" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="scoping" element={<Scoping />} />
        <Route path="controls" element={<Controls />} />
        <Route path="policies" element={<Policies />} />
        <Route path="policies/:policyId" element={<PolicyDetail />} />
        <Route path="evidence" element={<Evidence />} />
        <Route path="risks" element={<Risks />} />
        <Route path="audits" element={<Audits />} />
        <Route path="audits/:auditId" element={<AuditDetail />} />
        <Route path="incidents" element={<Incidents />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="team" element={<Team />} />
        <Route path="audit-pack" element={<AuditPack />} />
        <Route path="accreditation" element={<Accreditation />} />
        <Route path="registers" element={<Registers />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="training" element={<Training />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="trust" element={<TrustSettings />} />
        <Route path="support" element={<Support />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="ch_theme">
      <AuthProvider>
        <BrowserRouter>
          <div className="App"><AppRoutes /></div>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
